/** Typed client for the outreach-sequence API (LP-EMAIL-005 §7), built on apiClient. */
import { apiClient } from './apiClient';
import type {
  BulkEnrollResultItem,
  ContactSequenceHistory,
  DueTodayResponse,
  OutreachSequence,
  SequenceEnrollment,
  SequenceWritePayload,
} from '../types/sequence';

interface Paginated<T> {
  results?: T[];
}

function unwrap<T>(res: Paginated<T> | T[]): T[] {
  return Array.isArray(res) ? res : res.results ?? [];
}

export const sequenceService = {
  async listSequences(): Promise<OutreachSequence[]> {
    const res = await apiClient.get('/sequences/');
    return unwrap<OutreachSequence>(res);
  },

  async getSequence(id: string): Promise<OutreachSequence> {
    return apiClient.get(`/sequences/${id}/`);
  },

  async createSequence(payload: SequenceWritePayload): Promise<OutreachSequence> {
    return apiClient.post('/sequences/', payload);
  },

  async updateSequence(id: string, payload: Partial<SequenceWritePayload>): Promise<OutreachSequence> {
    return apiClient.patch(`/sequences/${id}/`, payload);
  },

  async deleteSequence(id: string): Promise<void> {
    return apiClient.delete(`/sequences/${id}/`);
  },

  async listPresets(verticalKey?: string): Promise<OutreachSequence[]> {
    const suffix = verticalKey ? `?vertical_key=${encodeURIComponent(verticalKey)}` : '';
    const res = await apiClient.get(`/sequences/presets/${suffix}`);
    return unwrap<OutreachSequence>(res);
  },

  async enroll(
    sequenceId: string,
    body: { contact_type: 'client' | 'lead'; contact_id: string; email_account_id?: string }
  ): Promise<SequenceEnrollment> {
    return apiClient.post(`/sequences/${sequenceId}/enroll/`, body);
  },

  async enrollBulk(
    sequenceId: string,
    body: { contacts: { contact_type: 'client' | 'lead'; contact_id: string }[]; email_account_id?: string }
  ): Promise<{ enrolled: number; results: BulkEnrollResultItem[] }> {
    return apiClient.post(`/sequences/${sequenceId}/enroll-bulk/`, body);
  },

  async listEnrollments(params?: {
    state?: string;
    contact_id?: string;
    sequence_id?: string;
  }): Promise<SequenceEnrollment[]> {
    const query = new URLSearchParams();
    if (params?.state) query.set('state', params.state);
    if (params?.contact_id) query.set('contact_id', params.contact_id);
    if (params?.sequence_id) query.set('sequence_id', params.sequence_id);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const res = await apiClient.get(`/sequences/enrollments/${suffix}`);
    return unwrap<SequenceEnrollment>(res);
  },

  async getEnrollment(id: string): Promise<SequenceEnrollment> {
    return apiClient.get(`/sequences/enrollments/${id}/`);
  },

  async stopEnrollment(id: string): Promise<SequenceEnrollment> {
    return apiClient.post(`/sequences/enrollments/${id}/stop/`, {});
  },

  async pauseEnrollment(id: string): Promise<SequenceEnrollment> {
    return apiClient.post(`/sequences/enrollments/${id}/pause/`, {});
  },

  async resumeEnrollment(id: string): Promise<SequenceEnrollment> {
    return apiClient.post(`/sequences/enrollments/${id}/resume/`, {});
  },

  async skipStep(enrollmentId: string, stepIndex: number): Promise<SequenceEnrollment> {
    return apiClient.post(`/sequences/enrollments/${enrollmentId}/steps/${stepIndex}/skip/`, {});
  },

  async dueToday(): Promise<DueTodayResponse> {
    return apiClient.get('/sequences/due-today/');
  },

  async getSettings(): Promise<{ sequences_paused: boolean }> {
    return apiClient.get('/sequences/settings/');
  },

  async pauseAll(paused: boolean): Promise<{ sequences_paused: boolean }> {
    return apiClient.post('/sequences/pause-all/', { paused });
  },

  async contactHistory(contactType: 'client' | 'lead', contactId: string): Promise<ContactSequenceHistory> {
    return apiClient.get(`/contacts/${contactType}/${contactId}/sequence-history/`);
  },
};
